import { FC, useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  Autocomplete,
  Option,
} from '@beeline/design-system-react/extracted-styles';

import { useFloorMap } from 'src/components/FloorMap/FloorMapProvider';
import { getFilteredRooms } from 'src/store/rooms/selectors';
import { openRoom } from 'src/store/rooms/slice';
import { useAppDispatch } from 'src/store/useAppDispatch';
import { PlaceType } from 'src/types';

const renderValue = (option: Option<string>): string => option.value ?? '';

export interface RoomSearchProps {
  onOpen?: () => void;
}

export const RoomSearch: FC<RoomSearchProps> = ({ onOpen }) => {
  const dispatch = useAppDispatch();

  const filteredRooms = useSelector(getFilteredRooms);

  const { scrollTo } = useFloorMap();

  const options = useMemo((): Option<string>[] => {
    const result: Option<string>[] = [];

    for (const [key, value] of filteredRooms) {
      if (!key || !value.title) continue;

      result.push({ id: key, value: value.title });
    }

    return result.sort((a, b) => a.value.localeCompare(b.value));
  }, [filteredRooms]);

  const [value, setValue] = useState<Option<string> | null>(null);
  const [input, setInput] = useState('');

  const filteredOptions = useMemo((): Option<string>[] => {
    const search = input.toLowerCase();

    return options.filter(option =>
      option.value.toLowerCase().includes(search),
    );
  }, [input, options]);

  const handleChange = useCallback(
    async (option: Option<string>) => {
      setValue(option);

      const found = filteredRooms.get(option.id as string);
      if (found && found.type === PlaceType.Room) {
        onOpen?.();

        dispatch(openRoom(found.mapPlaceId));

        scrollTo(found);
      }
    },
    [dispatch, filteredRooms, onOpen, scrollTo],
  );

  const handleClear = useCallback(async () => {
    setInput('');
    setValue(null);
  }, []);

  return (
    <Autocomplete
      placeholder='Комната'
      type='search'
      size='medium'
      disabled={!options.length}
      options={filteredOptions}
      value={value}
      noOptionsText='комнаты не найдены'
      onChange={handleChange}
      renderValue={renderValue}
      onInputChange={setInput}
      onInputClear={handleClear}
      loading={!options.length}
      fullWidth
    />
  );
};
